import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FilePlus2,
  ArrowRight,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import WizardStepper from "@/components/wizard/WizardStepper";
import TutorialDeepLink from "@/components/TutorialDeepLink";
import Dropzone from "@/components/wizard/Dropzone";
import FileCard from "@/components/wizard/FileCard";
import ExtractionReview from "@/components/wizard/ExtractionReview";
import {
  DOC_TYPES,
  createProject,
  uploadDocument,
  pollDocumentUntilReady,
} from "@/lib/documents";
import { pingFeature } from "@/lib/admin-api";

function uid() {
  return (
    (crypto?.randomUUID && crypto.randomUUID()) ||
    `id-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

/**
 * Wizard étape 1/5 — Chargement des documents
 * - Project metadata (name, country, fiscal_year) — auto-creates project on first upload
 * - Dropzone (PDF, max 5 files, 100 Mo)
 * - Per-file card with doc-type selector + progress + state
 * - Extraction review (editable) below, with confidence color coding
 * - "Valider et continuer" → étape 2 (non implémentée dans ce cycle)
 */
export default function NewProject() {
  const navigate = useNavigate();

  const [project, setProject] = useState({
    name: "",
    country: "",
    fiscal_year: "",
  });
  const [projectId, setProjectId] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [items, setItems] = useState([]); // [{localId, file, docType, status, ...}]

  const fiscalYearNum = useMemo(() => {
    const n = parseInt(project.fiscal_year, 10);
    return Number.isFinite(n) ? n : null;
  }, [project.fiscal_year]);

  const projectReady = project.name.trim().length >= 2;

  const ensureProject = useCallback(async () => {
    if (projectId) return projectId;
    if (!projectReady) {
      toast.error("Renseignez au moins un nom de projet (2 caractères min).");
      return null;
    }
    setCreatingProject(true);
    try {
      const p = await createProject({
        name: project.name.trim(),
        country: project.country.trim() || null,
        fiscal_year: fiscalYearNum,
      });
      setProjectId(p.id);
      toast.success("Projet créé. Vous pouvez maintenant ajouter des documents.");
      return p.id;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de créer le projet.");
      return null;
    } finally {
      setCreatingProject(false);
    }
  }, [projectId, projectReady, project, fiscalYearNum]);

  const onFilesSelected = useCallback(
    (valid, rejected) => {
      if (rejected?.length) {
        rejected.forEach((r) =>
          toast.error(`"${r.file.name}" rejeté (${r.reason}).`)
        );
      }
      if (!valid?.length) return;

      setItems((prev) => {
        const remainingSlots = Math.max(0, 5 - prev.length);
        if (remainingSlots <= 0) {
          toast.error("Maximum 5 fichiers par projet.");
          return prev;
        }
        const next = valid.slice(0, remainingSlots).map((f) => ({
          localId: uid(),
          file: f,
          docType: "Loi de Finances",
          status: "selected",
          uploadProgress: 0,
          serverDocId: null,
          extracted_data: null,
          quality_score: null,
          error: null,
          cached: false,
        }));
        if (valid.length > remainingSlots) {
          toast.warning(
            `Seuls ${remainingSlots} fichier(s) ajoutés (limite 5 au total).`
          );
        }
        return [...prev, ...next];
      });
    },
    []
  );

  const setItem = useCallback((localId, patch) => {
    setItems((prev) =>
      prev.map((it) =>
        it.localId === localId
          ? typeof patch === "function"
            ? patch(it)
            : { ...it, ...patch }
          : it
      )
    );
  }, []);

  const startUpload = useCallback(
    async (item, pid) => {
      setItem(item.localId, { status: "uploading", uploadProgress: 0, error: null });
      try {
        const resp = await uploadDocument({
          projectId: pid,
          file: item.file,
          docType: item.docType,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            // When upload completes, backend starts analyzing (no progress signal)
            if (pct >= 100) {
              setItem(item.localId, { status: "analyzing", uploadProgress: 100 });
            } else {
              setItem(item.localId, { uploadProgress: pct });
            }
          },
        });
        let serverDoc = resp.data;
        // If the backend returned a "cached" analyzed document instantly, use it directly.
        // Otherwise, the backend is processing asynchronously — poll until ready.
        if (!resp.cached && serverDoc.status !== "analyzed") {
          setItem(item.localId, { status: "analyzing", uploadProgress: 100 });
          serverDoc = await pollDocumentUntilReady(serverDoc.id, {
            onTick: (d) =>
              setItem(item.localId, {
                status: d.status === "extracting" ? "analyzing" : d.status,
              }),
          });
        }
        setItem(item.localId, {
          status: "analyzed",
          serverDocId: serverDoc.id,
          extracted_data: serverDoc.extracted_data,
          quality_score: serverDoc.quality_score,
          page_count: serverDoc.page_count,
          full_document: serverDoc,
          cached: !!resp.cached,
          uploadProgress: 100,
        });
        if (resp.cached) {
          toast.info(`"${item.file.name}" récupéré du cache (aucun appel LLM).`);
        } else {
          toast.success(`"${item.file.name}" analysé avec succès.`);
        }
        pingFeature("pdf.upload.analyzed", {
          project_id: pid,
          metadata: {
            doc_type: item.docType,
            cached: !!resp.cached,
            page_count: serverDoc.page_count || null,
            quality_score: serverDoc.quality_score || null,
          },
        });
      } catch (e) {
        const msg =
          e.response?.data?.detail || e.message || "Échec inconnu.";
        setItem(item.localId, { status: "failed", error: msg });
        toast.error(`Échec : ${msg}`);
      }
    },
    [setItem]
  );

  // Auto-trigger upload when a file moves to `selected` AND project is ready
  const uploadingRef = useRef(new Set());
  useEffect(() => {
    (async () => {
      const toProcess = items.filter(
        (it) => it.status === "selected" && !uploadingRef.current.has(it.localId)
      );
      if (toProcess.length === 0) return;
      const pid = await ensureProject();
      if (!pid) return;
      for (const it of toProcess) {
        uploadingRef.current.add(it.localId);
        // fire sequentially to avoid overwhelming the LLM budget
        // eslint-disable-next-line no-await-in-loop
        await startUpload(it, pid);
        uploadingRef.current.delete(it.localId);
      }
    })();
  }, [items, ensureProject, startUpload]);

  const onRemove = (localId) => {
    setItems((prev) => prev.filter((it) => it.localId !== localId));
  };

  const onRetry = async (localId) => {
    const it = items.find((x) => x.localId === localId);
    if (!it) return;
    const pid = await ensureProject();
    if (!pid) return;
    await startUpload(it, pid);
  };

  const analyzedDocs = items.filter(
    (it) => it.status === "analyzed" && it.full_document
  );
  const canContinue = analyzedDocs.length > 0 && !items.some(
    (it) => it.status === "uploading" || it.status === "analyzing"
  );

  const onPatched = (fresh) => {
    setItems((prev) =>
      prev.map((it) =>
        it.serverDocId === fresh.id
          ? {
              ...it,
              extracted_data: fresh.extracted_data,
              quality_score: fresh.quality_score,
              full_document: fresh,
            }
          : it
      )
    );
  };

  const handleContinue = () => {
    if (!projectId) {
      toast.error("Aucun projet à valider.");
      return;
    }
    navigate(`/app/projects/${projectId}/wizard/profiles`);
  };

  return (
    <div className="space-y-8" data-testid="new-project-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Nouveau projet · Étape 1/7
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Chargement des documents
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Déposez jusqu'à <strong>5 documents PDF</strong> (budgets, lois de
          finances, rapports d'audit, textes juridiques). Chaque fichier sera
          analysé une seule fois par <strong>GPT-4o (JSON mode)</strong>.
        </p>
      </div>

      <WizardStepper current={1} />
      <TutorialDeepLink
        chapter={2}
        section="section-2-2"
        id="wizard-step-1-upload"
        label="Première fois ? Voir comment uploader un PDF budgétaire"
        duration={8}
      />

      {/* Project info */}
      <Card data-testid="project-info-card" className="border-brand-gold/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FilePlus2 className="h-5 w-5 text-brand-gold" />
            Informations du projet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="proj-name">Nom du projet *</Label>
              <Input
                id="proj-name"
                className="mt-1.5"
                placeholder="Ex : Audit Loi de Finances 2024"
                value={project.name}
                onChange={(e) => setProject((p) => ({ ...p, name: e.target.value }))}
                disabled={!!projectId}
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label htmlFor="proj-country">Pays</Label>
              <Input
                id="proj-country"
                className="mt-1.5"
                placeholder="Sénégal"
                value={project.country}
                onChange={(e) => setProject((p) => ({ ...p, country: e.target.value }))}
                disabled={!!projectId}
                data-testid="project-country-input"
              />
            </div>
            <div>
              <Label htmlFor="proj-year">Année fiscale</Label>
              <Input
                id="proj-year"
                type="number"
                className="mt-1.5"
                placeholder="2024"
                value={project.fiscal_year}
                onChange={(e) =>
                  setProject((p) => ({ ...p, fiscal_year: e.target.value }))
                }
                disabled={!!projectId}
                data-testid="project-year-input"
              />
            </div>
          </div>
          {projectId && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="project-created-hint">
              Projet créé · ID <span className="font-mono">{projectId.slice(0, 8)}…</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Dropzone */}
      <Card data-testid="upload-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand-gold" />
            Documents à analyser ({items.length}/5)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Dropzone
            onFiles={onFilesSelected}
            maxFiles={5 - items.length}
            disabled={!projectReady || items.length >= 5 || creatingProject}
          />
          {!projectReady && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldAlert className="h-3.5 w-3.5" />
              Renseignez d'abord au moins un nom de projet.
            </p>
          )}

          {items.length > 0 && (
            <div className="grid grid-cols-1 gap-3" data-testid="file-list">
              {items.map((it) => (
                <FileCard
                  key={it.localId}
                  item={it}
                  onDocTypeChange={(t) => setItem(it.localId, { docType: t })}
                  onRemove={() => onRemove(it.localId)}
                  onRetry={() => onRetry(it.localId)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extraction reviews */}
      {analyzedDocs.length > 0 && (
        <div className="space-y-5" data-testid="extraction-reviews">
          <div>
            <h2 className="text-xl font-bold">Données extraites — revue & correction</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Corrigez toute valeur avant de passer à l'étape suivante. Les lignes
              à faible fiabilité sont signalées en rouge.
            </p>
          </div>
          {analyzedDocs.map((it) => (
            <ExtractionReview
              key={it.serverDocId}
              document={it.full_document}
              onPatched={onPatched}
            />
          ))}
        </div>
      )}

      {/* Continue CTA */}
      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-5">
        <p className="mr-auto text-xs text-muted-foreground">
          {analyzedDocs.length} document{analyzedDocs.length > 1 ? "s" : ""} analysé
          {analyzedDocs.length > 1 ? "s" : ""} · Minimum 1 pour continuer
        </p>
        <Button
          variant="outline"
          onClick={() => navigate("/app/dashboard")}
          data-testid="wizard-cancel-btn"
        >
          Annuler
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!canContinue}
          data-testid="wizard-continue-btn"
        >
          Valider et continuer
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// Keep DOC_TYPES imported so list is referenced (and easy to find for future maintainers).
// eslint-disable-next-line no-unused-vars
const _DOC_TYPES = DOC_TYPES;
